"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { VerificationTabs } from "./verification-tabs"
import { AccountSelection } from "./account-selection"
import { useAccountForm } from "@/src/hooks/useAccountForm"
import { accountBranchAPI } from "@/src/lib/api/accounts"
import type { VerificationType } from "@/src/types/account"

interface AccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  branchId: number
  accountId?: number // For edit mode
  onSuccess?: () => void
}

export function AccountDialog({ open, onOpenChange, branchId, accountId, onSuccess }: AccountDialogProps) {
  const {
    formData,
    errors,
    config,
    branches,
    isLoading,
    isLoadingConfig,
    isLoadingBranches,
    updateField,
    handleSubmit,
    reset,
  } = useAccountForm({
    accountId,
    branchId,
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
    onError: (error) => {
      console.error('Account save error:', error)
    },
  })

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
  // Track previously linked branches to diff on save
  const [prevLinkedBranchIds, setPrevLinkedBranchIds] = useState<string[]>([])

  // Pre-load existing linked branches when editing
  useEffect(() => {
    if (accountId && open) {
      accountBranchAPI.getLinkedBranches(accountId)
        .then(linked => {
          const ids = linked.map((b: any) => (b.branch_id ?? b.id)?.toString() ?? '').filter(Boolean)
          setSelectedAccounts(ids)
          setPrevLinkedBranchIds(ids)
        })
        .catch(err => console.error('[AccountDialog] Failed to load linked branches:', err))
    } else if (!open) {
      setSelectedAccounts([])
      setPrevLinkedBranchIds([])
    }
  }, [accountId, open])

  const getBranchAccountCount = (branch: any): number => {
    if (typeof branch?.account_count === 'number' && Number.isFinite(branch.account_count)) {
      return branch.account_count
    }

    if (typeof branch?.account_count === 'string') {
      const parsed = Number(branch.account_count)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }

    if (Array.isArray(branch?.accounts)) {
      return branch.accounts.length
    }

    return 0
  }

  const totalSystemAccounts = (branches || []).reduce(
    (sum, branch) => sum + getBranchAccountCount(branch),
    0
  )

  // Transform branches data for AccountSelection component
  const branchItems = [
    { id: "all", name: "ตรวจสอบทุกสาขาในระบบ", type: "all", count: totalSystemAccounts },
    ...(branches || []).map((b: any) => ({
      id: b.id?.toString() || '',
      name: b.branch_name || '',
      type: "branch",
      count: getBranchAccountCount(b),
    }))
  ]

  const handleSave = async () => {
    // In edit mode, sync branch linkages before saving account fields
    if (accountId) {
      const selected = selectedAccounts.filter(id => id !== 'all')
      const toRemove = prevLinkedBranchIds.filter(id => !selected.includes(id))
      const toAdd = selected.filter(id => !prevLinkedBranchIds.includes(id))
      try {
        await Promise.all([
          ...toRemove.map(id => accountBranchAPI.unlinkFromBranch(accountId, id)),
          ...toAdd.map(id => accountBranchAPI.linkToBranch(accountId, id)),
        ])
        setPrevLinkedBranchIds(selected)
      } catch (err) {
        console.error('[AccountDialog] Failed to update branch linkages:', err)
      }
    }
    await handleSubmit()
  }

  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  if (isLoadingConfig || isLoadingBranches) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 gap-0" style={{ backgroundColor: '#ffffff' }}>
        {/* Header */}
        <DialogHeader className="p-6 pb-4" style={{ backgroundColor: '#ffffff' }}>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              {accountId ? 'แก้ไขบัญชี' : 'เพิ่มบัญชี'}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 pb-6" style={{ backgroundColor: '#ffffff' }}>
          {/* Main Tabs */}
          <div className="grid grid-cols-4 gap-3 mb-6 border rounded-lg border-gray-400">
            <button
              onClick={() => updateField('account_source', 'bank')}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg ${
                formData.account_source === "bank"
                  ? "text-brand-primary"
                  : ""
              }`}
            >
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src="/icons/icon-bank-circle.png"
                  alt="Bank"
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none"
                  }}
                />
              </div>
              <span className="text-xs">บัญชีธนาคาร</span>
            </button>

            <button
              onClick={() => updateField('account_source', 'promptpay')}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg ${
                formData.account_source === "promptpay"
                  ? "text-brand-primary"
                  : ""
              }`}
            >
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src="/icons/icon-promptpay.png"
                  alt="PromptPay"
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none"
                  }}
                />
              </div>
              <span className="text-xs">พร้อมเพย์</span>
            </button>

            <button
              disabled
              className="flex flex-col items-center gap-2 p-3 rounded-lg opacity-50 cursor-not-allowed"
            >
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src="/icons/icon-shop-circle.png"
                  alt="Shop"
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none"
                  }}
                />
              </div>
              <span className="text-xs">ช้อปธนาคาร</span>
            </button>

            <button
              disabled
              className="flex flex-col items-center gap-2 p-3 rounded-lg opacity-50 cursor-not-allowed"
            >
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src="/icons/icon-truemoney.png"
                  alt="True Money"
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none"
                  }}
                />
              </div>
              <span className="text-xs text-nowrap">True Money</span>
            </button>
          </div>

          {formData.account_source === "bank" && (
            <>
              {/* Bank Selection */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  ธนาคาร<span className="text-red-500">*</span>
                </label>
                <Select 
                  value={formData.bank_code} 
                  onValueChange={(value) => updateField('bank_code', value)}
                >
                  <SelectTrigger 
                    className={errors.bank_code ? "border-red-500" : ""}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <SelectValue placeholder="คลิกเพื่อเลือก" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.banks.map((bank) => (
                      <SelectItem key={bank.code} value={bank.code}>
                        {bank.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.bank_code && (
                  <p className="text-xs text-red-500 mt-1">{errors.bank_code}</p>
                )}
              </div>

              {/* Verification Tabs */}
              <VerificationTabs 
                value={formData.verification_type as "account-number" | "account-name" | "both"} 
                onValueChange={(value) => updateField('verification_type', value as VerificationType)}
                accountNumber={formData.account_number}
                onAccountNumberChange={(value) => updateField('account_number', value)}
                thaiName={formData.account_name_th}
                onThaiNameChange={(value) => updateField('account_name_th', value)}
                englishName={formData.account_name_en}
                onEnglishNameChange={(value) => updateField('account_name_en', value)}
                errors={{
                  account_number: errors.account_number,
                  account_name_th: errors.account_name,
                }}
              />
            </>
          )}

          {formData.account_source === "promptpay" && (
            <>
              {/* PromptPay Type Selection */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  ประเภทเลขที่พร้อมเพย์<span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.promptpay_type}
                  onValueChange={(value: any) => updateField('promptpay_type', value)}
                >
                  <SelectTrigger 
                    className={errors.promptpay_type ? "border-red-500" : ""}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <SelectValue placeholder="คลิกเพื่อเลือก" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.account_types
                      .find(t => t.value === 'promptpay')
                      ?.promptpay_types?.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {errors.promptpay_type && (
                  <p className="text-xs text-red-500 mt-1">{errors.promptpay_type}</p>
                )}
              </div>

              {/* PromptPay ID Input */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  หมายเลขพร้อมเพย์<span className="text-red-500">*</span>
                </label>
                <Input 
                  placeholder={
                    formData.promptpay_type === 'phone' ? 'กรอกเบอร์โทรศัพท์' :
                    formData.promptpay_type === 'id-card' ? 'กรอกเลขบัตรประชาชน' :
                    'กรอกเลขผู้เสียภาษี'
                  }
                  value={formData.promptpay_id}
                  onChange={(e) => updateField('promptpay_id', e.target.value)}
                  className={errors.promptpay_id ? "border-red-500" : ""}
                />
                {errors.promptpay_id && (
                  <p className="text-xs text-red-500 mt-1">{errors.promptpay_id}</p>
                )}
              </div>

              {/* Account Name */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  ชื่อบัญชี<span className="text-red-500">*</span>
                </label>
                <Input 
                  placeholder="กรอกชื่อบัญชี"
                  value={formData.account_name}
                  onChange={(e) => updateField('account_name', e.target.value)}
                  className={errors.account_name ? "border-red-500" : ""}
                />
                {errors.account_name && (
                  <p className="text-xs text-red-500 mt-1">{errors.account_name}</p>
                )}
              </div>
            </>
          )}

          {formData.account_source === "shop" && (
            <>
              {/* Shop Type Selection */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  ช็อปธนาคาร<span className="text-red-500">*</span>
                </label>
                <Select>
                  <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                    <SelectValue placeholder="คลิกเพื่อเลือก" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scb">SCB Shop</SelectItem>
                    <SelectItem value="kbank">K PLUS Shop</SelectItem>
                    <SelectItem value="bbl">Bualuang mBanking</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* QR Code Upload */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  อัพโหลด QR Code รับเงิน
                </label>
                <Button 
                  variant="outline" 
                  className="w-full border-gray-400 text-brand-primary">
                  <img src="/icons/icon-qr-upload.svg" alt="Upload" className="mr-2" />
                  กดเพื่ออัพโหลด
                </Button>
              </div>

              {/* Shop Code Input */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  รหัสร้าน<span className="text-red-500">*</span>
                </label>
                <Input placeholder="กรอกรหัสร้าน" />
              </div>

              {/* Shop Name Input */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  ชื่อร้าน<span className="text-red-500">*</span>
                </label>
                <Input placeholder="กรอกชื่อร้าน" />
              </div>
            </>
          )}

          {formData.account_source === "truemoney" && (
            <>
              {/* True Money ID Input */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  หมายเลขโทรศัพท์ หรือ รหัสลูกค้า<span className="text-red-500">*</span>
                </label>
                <Input placeholder="กรอกหมายเลขโทรศัพท์ หรือ รหัสลูกค้า" />
              </div>

              {/* Condition Label */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  เงื่อนไขการตรวจสอบ
                </label>
                <p className="text-xs text-red-500">
                  ตรวจสอบเฉพาะสลิปจากธนาคาร ที่โอนเข้าบัญชีของ True Money Wallet
                </p>
              </div>
            </>
          )}

          {/* Account Selection */}
          <AccountSelection 
            accounts={branchItems}
            selectedAccounts={selectedAccounts}
            onToggleAccount={(accountId) => {
              if (accountId === "all") {
                // Toggle ALL: select all branches or deselect all
                if (selectedAccounts.includes("all")) {
                  setSelectedAccounts([])
                } else {
                  const allIds = branchItems.map(acc => acc.id)
                  setSelectedAccounts(allIds)
                }
              } else {
                // Toggle individual branch
                setSelectedAccounts(prev => {
                  const isCurrentlySelected = prev.includes(accountId)
                  let newSelection: string[]
                  
                  if (isCurrentlySelected) {
                    newSelection = prev.filter(id => id !== accountId && id !== "all")
                  } else {
                    newSelection = [...prev.filter(id => id !== "all"), accountId]
                    
                    const allBranchIds = branchItems
                      .filter(acc => acc.type !== "all")
                      .map(acc => acc.id)
                    const allBranchesSelected = allBranchIds.every(id => newSelection.includes(id))
                    
                    if (allBranchesSelected && allBranchIds.length > 0) {
                      newSelection = ["all", ...newSelection]
                    }
                  }
                  
                  return newSelection
                })
              }
            }}
          />

          {/* Actions */}
          <div className="grid grid-cols-3 items-center justify-between gap-3 mt-6">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              ยกเลิก
            </Button>
            <div></div>
            <Button
              onClick={handleSave}
              variant="default"
              disabled={isLoading}
            >
              {isLoading ? 'กำลังบันทึก...' : 'ยืนยัน'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
